import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { supabase, isSupabaseConfigured, supabaseHelpers, DB_TABLES } from '../contexts/supabase';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [files, setFiles] = useState({});
  
  const [terminMarktData, setTerminMarktData] = useState([]);
  const [spotMarktData, setSpotMarktData] = useState([]);
  const [provisionData, setProvisionData] = useState([]);
  
  const [dataSource, setDataSource] = useState('empty');
  const [dataValidation, setDataValidation] = useState({});

  const initialColumns = {
    evu: true,
    strom_slp: true,
    strom_rlm: true,
    gas_slp: true,
    gas_rlm: true,
    verbrauchsgrenzen_strom: true,
    verbrauchsgrenzen_gas: true,
    bestellungsrelevante_unterlagen: true,
    pflichtangaben: true,
    provisionsgrenze: true,
    auszahlung: true,
    vertragsunterlagen_nach_abschluss: true,
    ausgeschlossene_branchen: true,
    bemerkung: true,
    bonitaetsgrenze: true,
    mehr_mindermengen_in_prozent: true, 
    kuendigungsfrist: true,
    verlaengerung_aktiv_passiv: true,
    buendelung: true,
    vollmachten_erlaubt: true,
    wechsel_von_spot_zu_fest: true
  };

  const [selectedColumns, setSelectedColumns] = useState(initialColumns);

  const sanitizeColumns = (cols) => {
    const cleanCols = { ...initialColumns };
    Object.keys(cols).forEach(key => {
      if (Object.prototype.hasOwnProperty.call(initialColumns, key)) {
        cleanCols[key] = cols[key];
      }
    });
    return cleanCols;
  };

  const hasColumnSelectionChanges = useCallback(() => {
    return Object.keys(initialColumns).some(key => selectedColumns[key] !== initialColumns[key]);
  }, [selectedColumns]);

  const [supabaseStatus, setSupabaseStatus] = useState({
    configured: false,
    connected: false,
    error: null
  });

  const updateSupabaseConfig = (url, key) => {
    if (url && key) {
      localStorage.setItem('cbuy_supabase_url', url);
      localStorage.setItem('cbuy_supabase_key', key);
      window.location.reload();
    }
  };

  const disconnectSupabase = () => {
    localStorage.removeItem('cbuy_supabase_url');
    localStorage.removeItem('cbuy_supabase_key');
    window.location.reload();
  };

  useEffect(() => {
    const checkSupabaseConfig = async () => {
      const configured = isSupabaseConfigured();
      if (!configured) {
        setSupabaseStatus({ configured: false, connected: false, error: 'Supabase ist nicht konfiguriert.' });
        setDataSource('localStorage');
        return;
      }
      try {
        const testResult = await supabaseHelpers.testConnection();
        if (testResult.success) {
          setSupabaseStatus({ configured: true, connected: true, error: null });
        } else {
          setSupabaseStatus({ configured: true, connected: false, error: testResult.error });
          setDataSource('localStorage');
        }
      } catch (error) {
        setSupabaseStatus({ configured: true, connected: false, error: error.message });
        setDataSource('localStorage');
      }
    };
    checkSupabaseConfig();
  }, []);

  // Definiere createProviderRecord VOR seiner Verwendung, damit es im Scope verfügbar ist
  const createProviderRecord = (baseData = {}) => {
    return {
      anbieter_id: baseData.anbieter_id || `provider-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      evu: baseData.evu || '',
      marktart: baseData.marktart || '',
      strom_slp: baseData.strom_slp || '',
      strom_rlm: baseData.strom_rlm || '',
      gas_slp: baseData.gas_slp || '',
      gas_rlm: baseData.gas_rlm || '',
      verbrauchsgrenzen_strom: baseData.verbrauchsgrenzen_strom || '',
      verbrauchsgrenzen_gas: baseData.verbrauchsgrenzen_gas || '',
      bestellungsrelevante_unterlagen: baseData.bestellungsrelevante_unterlagen || '',
      pflichtangaben: baseData.pflichtangaben || '',
      provisionsgrenze: baseData.provisionsgrenze || '',
      auszahlung: baseData.auszahlung || '',
      vertragsunterlagen_nach_abschluss: baseData.vertragsunterlagen_nach_abschluss || '',
      ausgeschlossene_branchen: baseData.ausgeschlossene_branchen || '',
      bemerkung: baseData.bemerkung || '',
      bonitaetsgrenze: baseData.bonitaetsgrenze || '',
      mehr_mindermengen_in_prozent: baseData.mehr_mindermengen_in_prozent ?? '', 
      kuendigungsfrist: baseData.kuendigungsfrist || '',
      verlaengerung_aktiv_passiv: baseData.verlaengerung_aktiv_passiv || '',
      buendelung: baseData.buendelung || '',
      vollmachten_erlaubt: baseData.vollmachten_erlaubt || '',
      wechsel_von_spot_zu_fest: baseData.wechsel_von_spot_zu_fest || '',
      
      quelle: baseData.quelle || '',
      lastUpdated: baseData.lastUpdated || new Date().toISOString(),
      created_at: new Date().toISOString(),
      bietet_strom: baseData.bietet_strom || false,
      bietet_gas: baseData.bietet_gas || false,
      id: baseData.anbieter_id || `provider-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: baseData.evu || '',
      energyType: baseData.bietet_strom && baseData.bietet_gas ? 'Strom+Gas' : baseData.bietet_strom ? 'Strom' : baseData.bietet_gas ? 'Gas' : '',
      marketType: baseData.marktart || ''
    };
  };

  const loadLocalStorageFallback = () => {
    const savedFiles = localStorage.getItem('cbuy-global-files');
    const savedTerminMarkt = localStorage.getItem('cbuy-global-terminmarkt-data');
    const savedSpotMarkt = localStorage.getItem('cbuy-global-spotmarkt-data');
    const savedProvisions = localStorage.getItem('cbuy-global-provisions-data');
    const savedColumns = localStorage.getItem('cbuy-global-selected-columns');
    
    let dataLoaded = false;
    
    if (savedFiles) { setFiles(JSON.parse(savedFiles)); dataLoaded = true; }
    if (savedTerminMarkt) { setTerminMarktData(JSON.parse(savedTerminMarkt)); dataLoaded = true; }
    if (savedSpotMarkt) { setSpotMarktData(JSON.parse(savedSpotMarkt)); dataLoaded = true; }
    if (savedProvisions) { setProvisionData(JSON.parse(savedProvisions)); dataLoaded = true; }
    if (savedColumns) { 
      setSelectedColumns(sanitizeColumns(JSON.parse(savedColumns))); 
    }

    if (dataLoaded) setDataSource('localStorage');
  };

  const loadGlobalData = useCallback(async () => {
    if (!supabaseStatus.connected) {
      loadLocalStorageFallback();
      return;
    }

    try {
      // 1. Lade Status aus Supabase
      const [terminResult, spotResult, provisionsResult, filesResult, settingsResult] = await Promise.allSettled([
        supabaseHelpers.getTerminmarktProviders(),
        supabaseHelpers.getSpotmarktProviders(),
        supabaseHelpers.getProvisionsData(),
        supabaseHelpers.getFileMetadata(),
        supabaseHelpers.getSettings('column_selection')
      ]);

      // Prüfe ob Supabase Daten hat
      const supabaseHasData = (terminResult.status === 'fulfilled' && terminResult.value?.length > 0) ||
                              (spotResult.status === 'fulfilled' && spotResult.value?.length > 0);

      // ---------------------------------------------------------
      // AUTO-SYNC: Falls Supabase leer ist, synchronisiere lokale Daten
      // ---------------------------------------------------------
      if (!supabaseHasData) {
         console.log('☁️ Supabase ist leer. Prüfe auf lokale Daten zum Synchronisieren...');
         
         const localTermin = localStorage.getItem('cbuy-global-terminmarkt-data');
         const localSpot = localStorage.getItem('cbuy-global-spotmarkt-data');
         const localProv = localStorage.getItem('cbuy-global-provisions-data');
         const localFiles = localStorage.getItem('cbuy-global-files');
         const localSettings = localStorage.getItem('cbuy-global-selected-columns');

         let synced = false;

         // Terminmarkt sync
         if (localTermin) {
            const data = JSON.parse(localTermin);
            if (Array.isArray(data) && data.length > 0) {
               console.log(`Syncing ${data.length} Terminmarkt records...`);
               // WICHTIG: Daten bereinigen bevor sie gesendet werden
               const cleanData = data.map(item => createProviderRecord(item));
               await supabaseHelpers.saveTerminmarktProviders(cleanData);
               setTerminMarktData(cleanData);
               synced = true;
            }
         }
         
         // Spotmarkt sync
         if (localSpot) {
            const data = JSON.parse(localSpot);
            if (Array.isArray(data) && data.length > 0) {
               console.log(`Syncing ${data.length} Spotmarkt records...`);
               // WICHTIG: Daten bereinigen
               const cleanData = data.map(item => createProviderRecord(item));
               await supabaseHelpers.saveSpotmarktProviders(cleanData);
               setSpotMarktData(cleanData);
               synced = true;
            }
         }

         // Provisions sync
         if (localProv) {
            const data = JSON.parse(localProv);
            if (Array.isArray(data) && data.length > 0) {
               console.log(`Syncing ${data.length} Provisions records...`);
               // WICHTIG: Daten bereinigen
               const cleanData = data.map(item => createProviderRecord(item));
               await supabaseHelpers.saveProvisionsData(cleanData);
               setProvisionData(cleanData);
               synced = true;
            }
         }

         // Files Metadata sync
         if (localFiles) {
            const filesObj = JSON.parse(localFiles);
            const filesMap = {};
            for (const [key, val] of Object.entries(filesObj)) {
                if (val) {
                    filesMap[key] = val;
                    await supabaseHelpers.saveFileMetadata({
                        file_type: key,
                        file_name: val.name,
                        file_size: val.size,
                        mime_type: val.type,
                        created_at: val.uploadDate || new Date().toISOString()
                    });
                }
            }
            setFiles(filesMap);
            if (Object.keys(filesMap).length > 0) synced = true;
         }

         // Column Settings sync
         if (localSettings) {
             const settings = JSON.parse(localSettings);
             await supabase.from(DB_TABLES.SETTINGS).delete().eq('category', 'column_selection');
             const settingsToInsert = Object.entries(settings).map(([key, value]) => ({
                category: 'column_selection', setting_key: key, setting_value: JSON.stringify(value)
             }));
             if (settingsToInsert.length > 0) {
                await supabase.from(DB_TABLES.SETTINGS).insert(settingsToInsert);
                setSelectedColumns(sanitizeColumns(settings));
                synced = true;
             }
         }

         if (synced) {
            console.log('✅ Lokale Daten wurden erfolgreich nach Supabase synchronisiert!');
            setDataSource('supabase');
            return; // Wir haben Daten gesetzt, fertig.
         }
      }

      // ---------------------------------------------------------
      // Normales Laden aus Supabase (wenn Daten vorhanden oder kein Sync nötig)
      // ---------------------------------------------------------

      if (terminResult.status === 'fulfilled' && terminResult.value) setTerminMarktData(terminResult.value);
      if (spotResult.status === 'fulfilled' && spotResult.value) setSpotMarktData(spotResult.value);
      if (provisionsResult.status === 'fulfilled' && provisionsResult.value) setProvisionData(provisionsResult.value);

      if (filesResult.status === 'fulfilled' && filesResult.value) {
        const filesMap = {};
        filesResult.value.forEach(file => {
          filesMap[file.file_type] = {
            name: file.file_name,
            uploadDate: file.created_at,
            size: file.file_size,
            type: file.mime_type
          };
        });
        setFiles(filesMap);
      }

      if (settingsResult.status === 'fulfilled' && settingsResult.value) {
        const columnSettings = {};
        settingsResult.value.forEach(setting => {
          columnSettings[setting.setting_key] = JSON.parse(setting.setting_value);
        });
        if (Object.keys(columnSettings).length > 0) {
          setSelectedColumns(sanitizeColumns(columnSettings));
        }
      }

      const hasData = (terminResult.value?.length > 0) || (spotResult.value?.length > 0) || (provisionsResult.value?.length > 0);
      
      if (hasData) {
        setDataSource('supabase');
      }
    } catch (error) {
      console.error('Fehler beim Laden oder Synchronisieren aus Supabase:', error);
      // Wenn der Sync fehlschlägt, laden wir trotzdem die lokalen Daten, damit der Nutzer nicht vor einem leeren Schirm sitzt
      loadLocalStorageFallback();
    }
  }, [supabaseStatus.connected]);

  useEffect(() => {
    if (supabaseStatus.configured || supabaseStatus.connected === false) {
      loadGlobalData();
    }
  }, [loadGlobalData, supabaseStatus.configured, supabaseStatus.connected]);

  const saveToSupabase = async (fileType, data, fileInfo) => {
    if (!supabaseStatus.connected) {
      saveToLocalStorage(fileType, data, fileInfo);
      return;
    }

    try {
      if (fileType === 'terminMarkt') await supabaseHelpers.saveTerminmarktProviders(data);
      else if (fileType === 'spotMarkt') await supabaseHelpers.saveSpotmarktProviders(data);
      else if (fileType === 'provisions') await supabaseHelpers.saveProvisionsData(data);

      if (fileInfo) {
        await supabaseHelpers.saveFileMetadata({
          file_type: fileType,
          file_name: fileInfo.name,
          file_size: fileInfo.size,
          mime_type: fileInfo.type,
          created_at: fileInfo.uploadDate
        });
      }
    } catch (error) {
      console.error('Fehler beim Speichern in Supabase:', error);
      saveToLocalStorage(fileType, data, fileInfo);
      setSupabaseStatus(prev => ({ ...prev, connected: false, error: error.message }));
    }
  };

  const saveToLocalStorage = (fileType, data, fileInfo) => {
    if (fileType === 'terminMarkt') { setTerminMarktData(data); localStorage.setItem('cbuy-global-terminmarkt-data', JSON.stringify(data)); }
    else if (fileType === 'spotMarkt') { setSpotMarktData(data); localStorage.setItem('cbuy-global-spotmarkt-data', JSON.stringify(data)); }
    else if (fileType === 'provisions') { setProvisionData(data); localStorage.setItem('cbuy-global-provisions-data', JSON.stringify(data)); }

    if (fileInfo) {
      setFiles(prev => ({ ...prev, [fileType]: fileInfo }));
      localStorage.setItem('cbuy-global-files', JSON.stringify({ ...files, [fileType]: fileInfo }));
    }
  };

  const saveColumnSettings = async (columnSettings) => {
    const cleanSettings = sanitizeColumns(columnSettings);
    
    if (!supabaseStatus.connected) {
      localStorage.setItem('cbuy-global-selected-columns', JSON.stringify(cleanSettings));
      return;
    }
    try {
      await supabase.from(DB_TABLES.SETTINGS).delete().eq('category', 'column_selection');
      const settingsToInsert = Object.entries(cleanSettings).map(([key, value]) => ({
        category: 'column_selection', setting_key: key, setting_value: JSON.stringify(value)
      }));
      await supabase.from(DB_TABLES.SETTINGS).insert(settingsToInsert);
    } catch (error) {
      localStorage.setItem('cbuy-global-selected-columns', JSON.stringify(cleanSettings));
    }
  };

  useEffect(() => {
    if (dataSource === 'supabase') saveColumnSettings(selectedColumns);
    else localStorage.setItem('cbuy-global-selected-columns', JSON.stringify(selectedColumns));
  }, [selectedColumns, dataSource]);

  const normalizeProviderName = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name.trim().replace(/[^\w\s\-.]/g, '').toLowerCase().replace(/ü/g, 'ue').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ß/g, 'ss');
  };

  const findValueByColumnName = (row, columnName) => {
    const target = columnName.toLowerCase().trim();
    for (const header of Object.keys(row)) {
      if (header && header.trim().toLowerCase() === target) {
        const value = row[header];
        if (value !== undefined && value !== null) {
          const strVal = value.toString().trim();
          if (strVal !== '') return strVal;
        }
      }
    }
    return '';
  };

  // ROBUSTE MM-Spalten-Erkennung - mehrfache Strategien
  const findMehrMinderColumn = (row) => {
    console.log('DEBUG: Suche MM-Spalte. Verfügbare Header:', Object.keys(row));
    
    // Strategie 1: Direkter Index-Zugriff (Spalte T = Index 19)
    const values = Object.values(row);
    if (values.length > 19) {
      const columnTValue = values[19]; // Spalte T (0-basiert)
      if (columnTValue !== undefined && columnTValue !== null && columnTValue.toString().trim() !== '') {
        console.log('DEBUG: MM aus Spalte T gefunden:', columnTValue);
        return columnTValue.toString();
      }
    }
    
    // Strategie 2: Header-Normalisierung und exakte Übereinstimmung
    const normalizeHeader = (header) => {
      // Use new RegExp to avoid ESLint escape character warning
      const separatorRegex = new RegExp('[\\/\\-‐‒–—]', 'g');
      
      return header.toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .replace(separatorRegex, '-')
        .replace(/%/g, '%');
    };
    
    const validHeaders = [
      'mehr-mindermengen mit %',
      'mehr-/mindermengen in %',
      'mehr / mindermengen %',
      'mehr-mindermengen %'
    ];
    
    for (const [rawHeader, value] of Object.entries(row)) {
      const normalizedHeader = normalizeHeader(rawHeader);
      
      if (validHeaders.includes(normalizedHeader)) {
        if (value !== undefined && value !== null && value.toString().trim() !== '') {
          console.log('DEBUG: MM über Header gefunden:', rawHeader, '->', value);
          return value.toString();
        }
      }
    }
    
    // Strategie 3: Enthält "mehr" UND "%"
    for (const [header, value] of Object.entries(row)) {
      const normalizedHeader = normalizeHeader(header);
      if (normalizedHeader.includes('mehr') && normalizedHeader.includes('%')) {
        if (value !== undefined && value !== null && value.toString().trim() !== '') {
          console.log('DEBUG: MM über "mehr" + "%" gefunden:', header, '->', value);
          return value.toString();
        }
      }
    }
    
    // Strategie 4: Nur "mehr" enthalten (letzter Fallback)
    for (const [header, value] of Object.entries(row)) {
      const normalizedHeader = normalizeHeader(header);
      if (normalizedHeader.includes('mehr')) {
        if (value !== undefined && value !== null && value.toString().trim() !== '') {
          console.log('DEBUG: MM über "mehr" gefunden:', header, '->', value);
          return value.toString();
        }
      }
    }
    
    console.log('DEBUG: Keine MM-Spalte gefunden');
    return '';
  };

  const parseTerminmarktSheet = (sheetData, sheetName) => {
    const processedProviders = [];
    sheetData.forEach((row) => {
      if (Object.prototype.hasOwnProperty.call(row, 'Unnamed: 0')) delete row['Unnamed: 0'];
      const evu = findValueByColumnName(row, 'EVU');
      if (!evu) return;
      const strom_slp = findValueByColumnName(row, 'Strom SLP');
      const strom_rlm = findValueByColumnName(row, 'Strom RLM');
      const gas_slp = findValueByColumnName(row, 'Gas SLP');
      const gas_rlm = findValueByColumnName(row, 'Gas RLM');
      
      const providerData = {
        evu: evu,
        marktart: 'Terminmarkt',
        strom_slp, strom_rlm, gas_slp, gas_rlm,
        verbrauchsgrenzen_strom: findValueByColumnName(row, 'Verbrauchsgrenzen STROM'),
        verbrauchsgrenzen_gas: findValueByColumnName(row, 'Verbrauchsgrenzen GAS'),
        bestellungsrelevante_unterlagen: findValueByColumnName(row, 'Bestellungsrelevante Unterlagen:'),
        pflichtangaben: findValueByColumnName(row, 'Pflichtangaben:'),
        provisionsgrenze: findValueByColumnName(row, 'Provisionsgrenze:'),
        auszahlung: findValueByColumnName(row, 'Auszahlung:'),
        vertragsunterlagen_nach_abschluss: findValueByColumnName(row, 'Vertragsunterlagen nach Abschluss:'),
        ausgeschlossene_branchen: findValueByColumnName(row, 'Ausgeschlossene Branchen'),
        bemerkung: findValueByColumnName(row, 'Bemerkung:'),
        bonitaetsgrenze: findValueByColumnName(row, 'Bonitätsgrenze'),
        
        // WICHTIG: Robuste MM-Suche
        mehr_mindermengen_in_prozent: findMehrMinderColumn(row),

        kuendigungsfrist: findValueByColumnName(row, 'Kündigungsfrist'),
        verlaengerung_aktiv_passiv: findValueByColumnName(row, 'Verlängerung / aktiv oder passiv'),
        buendelung: findValueByColumnName(row, 'Bündelung'),
        vollmachten_erlaubt: findValueByColumnName(row, 'Vollmachten erlaubt?'),
        
        bietet_strom: (strom_slp !== '' || strom_rlm !== ''),
        bietet_gas: (gas_slp !== '' || gas_rlm !== ''),
        quelle: sheetName,
        lastUpdated: new Date().toISOString()
      };
      processedProviders.push(createProviderRecord(providerData));
    });
    return { providers: processedProviders, errors: [] };
  };

  const parseSpotmarktSheet = (sheetData, sheetName) => {
    const processedProviders = [];
    sheetData.forEach((row) => {
      if (Object.prototype.hasOwnProperty.call(row, 'Unnamed: 0')) delete row['Unnamed: 0'];
      const evu = findValueByColumnName(row, 'EVU');
      if (!evu) return;
      const strom_slp = findValueByColumnName(row, 'Strom SLP');
      const strom_rlm = findValueByColumnName(row, 'Strom RLM');
      const gas_slp = findValueByColumnName(row, 'Gas SLP');
      const gas_rlm = findValueByColumnName(row, 'Gas RLM');

      const providerData = {
        evu: evu,
        marktart: 'Spotmarkt',
        strom_slp, strom_rlm, gas_slp, gas_rlm,
        verbrauchsgrenzen_strom: findValueByColumnName(row, 'Verbrauchsgrenzen STROM'),
        verbrauchsgrenzen_gas: findValueByColumnName(row, 'Verbrauchsgrenzen GAS'),
        bestellungsrelevante_unterlagen: findValueByColumnName(row, 'Bestellungsrelevante Unterlagen:'),
        pflichtangaben: findValueByColumnName(row, 'Pflichtangaben:'),
        provisionsgrenze: findValueByColumnName(row, 'Provisionsgrenze:'),
        auszahlung: findValueByColumnName(row, 'Auszahlung:'),
        vertragsunterlagen_nach_abschluss: findValueByColumnName(row, 'Vertragsunterlagen nach Abschluss:'),
        ausgeschlossene_branchen: findValueByColumnName(row, 'Ausgeschlossene Branchen'),
        bemerkung: findValueByColumnName(row, 'Bemerkung:'),
        bonitaetsgrenze: findValueByColumnName(row, 'Bonitätsgrenze'),
        
        mehr_mindermengen_in_prozent: findMehrMinderColumn(row),

        kuendigungsfrist: findValueByColumnName(row, 'Kündigungsfrist'),
        verlaengerung_aktiv_passiv: findValueByColumnName(row, 'Verlängerung / aktiv oder passiv'),
        buendelung: findValueByColumnName(row, 'wie wird gebündelt?'),
        vollmachten_erlaubt: findValueByColumnName(row, 'Vollmachten erlaubt?'),
        wechsel_von_spot_zu_fest: findValueByColumnName(row, 'Wechsel von SPOT zu FEST möglich?'),
        bietet_strom: (strom_slp !== '' || strom_rlm !== ''),
        bietet_gas: (gas_slp !== '' || gas_rlm !== ''),
        quelle: sheetName,
        lastUpdated: new Date().toISOString()
      };
      processedProviders.push(createProviderRecord(providerData));
    });
    return { providers: processedProviders, errors: [] };
  };

  const parseProvisionsSheet = (sheetData, sheetName) => {
    return { providers: [], errors: [] };
  };

  const mergeAllProviderData = () => {
    const allProviders = new Map();
    [...terminMarktData, ...spotMarktData].forEach(provider => {
      allProviders.set(normalizeProviderName(provider.evu), { ...provider });
    });
    return Array.from(allProviders.values());
  };

  const enrichWithProvisionData = useCallback((provider) => {
    return provider;
  }, [provisionData]);

  const performDataValidation = useCallback(() => {
    const validation = { terminmarktErrors: [], spotmarktErrors: [], provisionsErrors: [], matchingErrors: [], duplicateWarnings: [] };
    const checkDups = (data, type) => {
      const dups = {};
      data.forEach(p => {
        const n = normalizeProviderName(p.evu);
        if(!dups[n]) dups[n] = [];
        dups[n].push(p);
      });
      Object.entries(dups).forEach(([n, ps]) => {
        if(ps.length > 1) validation.duplicateWarnings.push(`Mehrfachzeilen für EVU "${ps[0].evu}" in ${type}-Datei.`);
      });
    };
    checkDups(terminMarktData, 'Terminmarkt');
    checkDups(spotMarktData, 'Spotmarkt');
    
    setDataValidation(validation);
    return validation;
  }, [terminMarktData, spotMarktData, provisionData]);

  const processFile = async (file, fileType) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { cellDates: true, cellNF: false, cellText: false, dense: true });
      const sheetName = workbook.SheetNames[0];
      const targetSheetName = fileType === 'provisions' ? sheetName : (workbook.SheetNames.includes('Gesamt') ? 'Gesamt' : sheetName);
      const worksheet = workbook.Sheets[targetSheetName];
      const sheetData = XLSX.utils.sheet_to_json(worksheet, { raw: false, defval: '', blankrows: false });
      
      if (sheetData.length === 0) return { providers: [], errors: ['Keine Daten gefunden'] };
      
      let result = { providers: [], errors: [] };
      if (fileType === 'terminMarkt') {
        result = parseTerminmarktSheet(sheetData, targetSheetName);
        setTerminMarktData(result.providers);
      } else if (fileType === 'spotMarkt') {
        result = parseSpotmarktSheet(sheetData, targetSheetName);
        setSpotMarktData(result.providers);
      } else if (fileType === 'provisions') {
        result = { providers: [], errors: [] }; 
        setProvisionData([]);
      }

      await saveToSupabase(fileType, result.providers, {
        name: file.name, size: file.size, type: file.type, uploadDate: new Date().toISOString()
      });
      
      if (supabaseStatus.connected) setDataSource('supabase');
      return result;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const getFilteredProviders = useCallback((marktart, energieart) => {
    let sourceData = marktart === 'terminmarkt' ? terminMarktData : spotMarktData;
    if (sourceData.length === 0) return [];
    return sourceData.filter(p => energieart === 'strom' ? p.bietet_strom : p.bietet_gas).map(enrichWithProvisionData);
  }, [terminMarktData, spotMarktData, enrichWithProvisionData]);

  const uploadFile = async (fileType, file) => {
    const result = await processFile(file, fileType);
    setTimeout(() => performDataValidation(), 100);
    return result;
  };

  const getProviderById = (id) => {
    const provider = [...terminMarktData, ...spotMarktData].find(item => item.id === id);
    if (provider) return enrichWithProvisionData(provider);
    return null;
  };

  const getAllProviders = () => mergeAllProviderData();
  const hasFile = (fileType) => files[fileType] && files[fileType].uploadDate;
  const renderValue = (value, fallback = '–') => value && value.toString().trim() !== '' ? value.toString().trim() : fallback;
  const updateSelectedColumn = (column, selected) => setSelectedColumns(prev => ({ ...prev, [column]: selected }));
  
  const selectAllColumns = () => {
    const newCols = { ...selectedColumns };
    Object.keys(newCols).forEach(k => newCols[k] = true);
    setSelectedColumns(newCols);
  };
  
  const clearAllColumns = () => {
    const newCols = { ...selectedColumns };
    Object.keys(newCols).forEach(k => {
      if(k !== 'evu') newCols[k] = false;
    });
    setSelectedColumns(newCols);
  };

  const columnDisplayNames = {
    evu: 'EVU',
    strom_slp: 'Strom SLP', strom_rlm: 'Strom RLM',
    gas_slp: 'Gas SLP', gas_rlm: 'Gas RLM',
    verbrauchsgrenzen_strom: 'Verbrauchsgrenzen Strom', verbrauchsgrenzen_gas: 'Verbrauchsgrenzen Gas',
    bestellungsrelevante_unterlagen: 'Bestellungsunterlagen', pflichtangaben: 'Pflichtangaben',
    provisionsgrenze: 'Provisionsgrenze', auszahlung: 'Auszahlung',
    vertragsunterlagen_nach_abschluss: 'Vertragsunterlagen', ausgeschlossene_branchen: 'Ausgeschlossene Branchen',
    bemerkung: 'Bemerkung', bonitaetsgrenze: 'Bonitätsgrenze',
    mehr_mindermengen_in_prozent: 'Mehr-/Mindermengen in %', 
    kuendigungsfrist: 'Kündigungsfrist', verlaengerung_aktiv_passiv: 'Verlängerung',
    buendelung: 'Bündelung', vollmachten_erlaubt: 'Vollmachten',
    wechsel_von_spot_zu_fest: 'Wechsel Spot→Fest'
  };

  const resetAllGlobalData = async () => {
    if (supabaseStatus.connected) {
      await supabase.from(DB_TABLES.TERMINMARKT).delete().gte('created_at', new Date(0).toISOString());
      await supabase.from(DB_TABLES.SPOTMARKT).delete().gte('created_at', new Date(0).toISOString());
      await supabase.from(DB_TABLES.PROVISIONS).delete().gte('created_at', new Date(0).toISOString());
      await supabase.from(DB_TABLES.FILES).delete().gte('created_at', new Date(0).toISOString());
      await supabase.from(DB_TABLES.SETTINGS).delete().eq('category', 'column_selection');
    }
    localStorage.removeItem('cbuy-global-files');
    localStorage.removeItem('cbuy-global-terminmarkt-data');
    localStorage.removeItem('cbuy-global-spotmarkt-data');
    localStorage.removeItem('cbuy-global-provisions-data');
    localStorage.removeItem('cbuy-global-selected-columns');
    setFiles({}); setTerminMarktData([]); setSpotMarktData([]); setProvisionData([]); setDataSource('empty'); setDataValidation({});
  };

  const getGlobalDataStatus = () => ({
    dataSource, hasSupabaseData: dataSource === 'supabase', hasLocalStorageData: dataSource === 'localStorage',
    terminMarktCount: terminMarktData.length, spotmarktCount: spotMarktData.length, provisionsCount: provisionData.length,
    filesCount: Object.keys(files).length, isMultiUserReady: supabaseStatus.connected, cloudSyncActive: supabaseStatus.connected, supabaseStatus
  });

  return (
    <DataContext.Provider value={{
      files, terminMarktData, spotMarktData, provisionData, dataSource, dataValidation,
      selectedColumns, columnDisplayNames, getFilteredProviders, uploadFile, getProviderById, getAllProviders,
      hasFile, renderValue, mergeAllProviderData, performDataValidation, updateSelectedColumn, selectAllColumns,
      clearAllColumns, hasColumnSelectionChanges, resetAllGlobalData, getGlobalDataStatus, supabaseStatus, updateSupabaseConfig, disconnectSupabase
    }}>
      {children}
    </DataContext.Provider>
  );
};